/**
 * Labstep
 *
 * @module components/Entity/Table/DragSortable
 * @description A table with sortable rows
 */

import classnames from 'classnames';
import { EntitySortableTable } from 'labstep-web/components/Entity/Sortable/Table';
import { SortableItemDragHandle as DragHandle } from 'labstep-web/core/Sortable/Item/DragHandle';
import { SortableItemTable } from 'labstep-web/core/Sortable/Item/Table';
import { TableSimpleCollapsible } from 'labstep-web/core/Table/Simple/Collapsible';
import type { Metadata } from 'labstep-web/models/metadata';
import type React from 'react';
import { Table } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type {
  EntityTableDragSortableBody,
  EntityTableDragSortableProps,
} from './types';

export const entityTableDragSortableBody: EntityTableDragSortableBody =
  (sortable, disabled, additionalItemRows) =>
  ({ rows, columns }) => (
    <EntitySortableTable
      className={classnames(styles.body, styles.borderless)}
      entityName={sortable}
      items={rows}
      disabled={disabled}
      isTableBody={!additionalItemRows}
      renderItem={(item) => (
        <SortableItemTable
          key={item.id}
          id={item.id}
          additionalRows={additionalItemRows?.(item as Metadata)}
        >
          {columns.map((column, index) => (
            // FIXME react/no-array-index-key
            <Table.Cell key={index} {...column.cellProps}>
              {index === 0 ? (
                <DragHandle>{column.content(item)}</DragHandle>
              ) : (
                column.content(item)
              )}
            </Table.Cell>
          ))}
        </SortableItemTable>
      )}
    />
  );

export const EntityTableDragSortable: React.FC<
  EntityTableDragSortableProps
> = ({
  sections,
  columns,
  sortable,
  disabled,
  additionalItemRows,
  ...rest
}) => {
  const sortableSections = sections
    ? sections.map((section) => ({
        ...section,
        customBody:
          section.customBody ||
          entityTableDragSortableBody(
            sortable,
            disabled,
            additionalItemRows,
          ),
      }))
    : [];

  return (
    <TableSimpleCollapsible
      columns={columns}
      sections={sortableSections}
      {...rest}
    />
  );
};
